// @flow
import classnames from 'classnames';
import React, {Component, Fragment} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import gql from 'graphql-tag';
import {injectIntl, FormattedMessage} from 'react-intl';
import {isEqual} from 'lodash';
import {SVGIcon} from '@homeaway/react-svg';
import {FILTER__16, CHEVRON_LEFT__16} from '@homeaway/svg-defs';
import {Checkbox} from '@homeaway/react-form-components';
import routerActions from '../../actions/router';
import mapAction from '../../actions/map';
import {filterCount} from '../../utils/filterCount';
import AsyncGoogleMap from './AsyncGoogleMap';
import SearchAreaButton, {type SearchAreaButtonProps} from './SearchAreaButton';
import {trigger, GENERIC_EVENT} from '../../utils/edapWrapper';
import {media} from '../../utils/breakpoints';
import {FILTERS_MODAL} from '../../actions/modals';
import {fragments as mapHitFragments} from '../MapHit/MapHit';
import {MAP_VIEW} from '../../constants/views';

import type {ListingShape as MapListingShape} from '../MapHit/MapHit';
import type {GoogleMapState} from './AsyncGoogleMap';
import type {State as ReduxState} from '../../reducers/index';
import type {IntlShape} from 'react-intl';
import type {Breakpoint} from '../../utils/breakpoints';

import './Map.less';
import LoadingPlaceholder from '../LoadingPlaceholder/LoadingPlaceholder';

export type MapViewport = {
    neLat: number,
    neLong: number,
    swLat: number,
    swLong: number
};

export type MapCentralPin = {
    lat?: number,
    lng?: number
};

const commonSelectionSet = `
    mapViewport {
        neLat
        neLong
        swLat
        swLong
    }
    page
    pageSize
    listings {
        ...MapHitListing
    }
    pinnedListing {
        listing {
            ...MapHitListing
        }
    }
`;

const commonFragments = gql`
    ${mapHitFragments.listing}
`;

export const fragments = {
    results: gql`
        fragment MapResults on Results {
            ${commonSelectionSet}
        }
        ${commonFragments}
    `,
    searchResult: gql`
        fragment MapSearchResult on SearchResult {
            ${commonSelectionSet}
        }
        ${commonFragments}
    `
};

export type MapMoveHandler = ({
    bounds: MapViewport,
    zoom: number
}) => void;

type MapOptions = {|
    zoomControl?: boolean
|};

type DispatchProps = {
    addPinClicked: typeof mapAction.addPinClicked,
    changeHitPreview: typeof mapAction.changeHitPreview,
    disableSearchOnMove: typeof mapAction.disableSearchOnMove,
    enableSearchOnMove: typeof mapAction.enableSearchOnMove,
    mapIdle: typeof mapAction.mapIdle,
    mapLoaded: typeof mapAction.mapLoaded,
    mapSearch: typeof routerActions.mapSearch,
    onClickFiltersButton: Function,
    resetSearchButton: typeof mapAction.mapResetSearchButton,
    setMapMoveState: typeof mapAction.setMapMoveState,
    showMapSearchButton: typeof mapAction.mapShowSearchButton,
    syncBounds: typeof mapAction.syncBounds,
    updateMap: typeof mapAction.updateMap
};

type StateProps = {
    fullLocale: string,
    bounds?: MapViewport,
    breakpoint: Breakpoint,
    currentHitPrev: string,
    highlightedPin: ?string,
    isRedirecting: boolean,
    isSearchButtonVisible: boolean,
    mapCentralPin?: MapCentralPin,
    mapMarkersClicked: Object,
    numFilters: number,
    shouldDisableSearchButton: boolean,
    shouldSearchOnMove: boolean,
    fitBoundsOverride: boolean,
    zoom: number,
    isTouchDevice: boolean,
    isMapView: boolean
};

type Props = {
    allowUpdate: boolean,
    changeViewport: Function,
    listings: MapListingShape[],
    intl: IntlShape,
    loading: boolean,
    onBackClick?: () => void,
    options: MapOptions,
    q: string,
    search: Function,
    shouldShowFiltersButton: boolean,
    siteName: string,
    isBoundingBoxSearch: boolean,
    page?: number,
    pageSize?: number
} & SearchAreaButtonProps &
    DispatchProps &
    StateProps;

type State = {
    isSearchButtonVisible: boolean
};

export const MAP_VIEW_CLASSNAME = 'map-view';
export class Map extends Component<Props, State> {
    static defaultProps = {
        // `options` is of type MapOptions, which as above is defined as a sealed
        // object type. Empty object literals are not "sealed" in Flow, meaning
        // it's legal to add unknown properties to them (this is not permitted for
        // object literals defined with a shape like `const myObj = {a: 1, b: 2}`,
        // see https://flow.org/try/#0G4QwTgBCELwQ3iAXBAjAXwNwCgQDoAjWCAFkyA
        // for an example). Freezing the empty object "seals" it as far as Flow
        // is concerned.
        // c.f. https://github.com/facebook/flow/issues/2977#issuecomment-390613203
        options: Object.freeze({})
    };

    state = {
        isSearchButtonVisible: false
    };

    componentDidUpdate() {
        // This allows us to use css to target the mobile download banner which sits outside of react
        /* istanbul ignore else */

        if (typeof window !== 'undefined') {
            const {isTouchDevice, isMapView} = this.props;
            const body = window.document.body;
            if (isTouchDevice && isMapView && body && body.classList) {
                body.classList.add(MAP_VIEW_CLASSNAME);
            } else {
                body.classList.remove(MAP_VIEW_CLASSNAME);
            }
        }
    }

    handleMapMoveEnd = (): void => {
        trigger('search.map.move', {
            highfrequency: 'true'
        });
    };

    handleAutoSearchCheck = (event: SyntheticEvent<HTMLInputElement>): void => {
        if (!event || !event.currentTarget) {
            return;
        }
        const currentTarget: HTMLInputElement = event.currentTarget;
        if (currentTarget.checked) {
            this.props.enableSearchOnMove();

            const {bounds, zoom, mapSearch} = this.props;
            mapSearch({
                ...bounds,
                zoom
            });
        } else {
            this.props.disableSearchOnMove();
        }

        trigger(GENERIC_EVENT, {
            eventaction: 'auto search checkbox',
            eventlabel: currentTarget.checked ? 'checked' : 'unchecked'
        });
    };

    handleMapMove: (GoogleMapState) => void = (googleMapState) => {
        const newZoom = googleMapState.zoom;
        const newBounds = googleMapState.bounds;
        const {bounds, zoom, updateMap} = this.props;

        if (zoom !== newZoom) {
            const zoomtype = zoom > newZoom ? 'out' : 'in';
            trigger('search.map.zoom', {
                zoomtype
            });
        }

        /* Note: auto update happens here in this updateMap action */
        if (!isEqual(newBounds, bounds) || !isEqual(newZoom, zoom)) {
            updateMap(newBounds, newZoom);
        }
    };

    handleSearch = () => {
        const {bounds, zoom, mapSearch, resetSearchButton} = this.props;

        resetSearchButton();
        if (bounds) {
            mapSearch({
                ...bounds,
                zoom
            });
            this.props.syncBounds(bounds);
        }
    };

    handleBackClick = () => {
        trigger(GENERIC_EVENT, {
            eventcategory: 'search',
            eventaction: 'map to list click',
            eventlabel: 'button'
        });

        /* istanbul ignore else */
        if (this.props.onBackClick) {
            this.props.onBackClick();
        }
    };

    handleFiltersButtonClick = () => {
        trigger(GENERIC_EVENT, {
            eventaction: 'filter menu clicked',
            eventcategory: 'search'
        });

        this.props.onClickFiltersButton();
    };

    render() {
        let {
            addPinClicked,
            allowUpdate,
            bounds,
            breakpoint,
            changeHitPreview,
            currentHitPrev,
            fitBoundsOverride,
            fullLocale,
            highlightedPin,
            intl,
            isBoundingBoxSearch,
            isRedirecting,
            isSearchButtonVisible,
            isTouchDevice,
            listings,
            loading,
            mapCentralPin,
            mapIdle,
            mapLoaded,
            mapMarkersClicked,
            numFilters,
            onBackClick,
            options,
            setMapMoveState,
            shouldDisableSearchButton,
            shouldSearchOnMove,
            shouldShowFiltersButton,
            showMapSearchButton,
            siteName,
            zoom,
            page,
            pageSize
        } = this.props;

        const mapClassNames = classnames('Map', {Map__loading: loading});
        const isDesktop = media(breakpoint, {minWidth: 'MD_MIN'});
        const isMobile = !media(breakpoint, {minWidth: 'SM_MIN'});

        return (
            <div className={mapClassNames} data-wdio="Map">
                {onBackClick && (
                    <button
                        data-wdio="Map__backButton"
                        className={classnames(
                            'Map__control',
                            'Map__control--top-left',
                            'Map__control--back-button'
                        )}
                        onClick={this.handleBackClick}
                    >
                        <SVGIcon
                            className="text-link"
                            markup={CHEVRON_LEFT__16}
                        />
                        <FormattedMessage
                            id="backToListButton"
                            description="Back to List button on map page"
                            defaultMessage="List"
                        />
                    </button>
                )}
                {shouldShowFiltersButton && (
                    <button
                        className={classnames(
                            'Map__control',
                            'Map__control--top-right',
                            'Map__control--filters-button'
                        )}
                        data-wdio="Map__filtersButton"
                        onClick={this.handleFiltersButtonClick}
                    >
                        <SVGIcon markup={FILTER__16} className="text-link" />
                        <FormattedMessage
                            id="filtersButton"
                            description="Button to open Filters menu"
                            defaultMessage="Filters {filterNumber}"
                            values={{
                                filterNumber: filterCount(numFilters)
                            }}
                        />
                    </button>
                )}
                {bounds ? (
                    <Fragment>
                        <AsyncGoogleMap
                            autoSearchEnabled={shouldSearchOnMove}
                            allowUpdate={allowUpdate}
                            bounds={bounds}
                            changeHitPreview={changeHitPreview}
                            containerElement={
                                <div className="Map__container" />
                            }
                            currentHitPrev={currentHitPrev}
                            fullLocale={fullLocale}
                            highlightedPin={highlightedPin}
                            listings={listings}
                            loading={loading}
                            intl={intl}
                            mapCentralPin={mapCentralPin}
                            mapElement={<div className="Map__map" />}
                            mapMarkersClicked={mapMarkersClicked}
                            onClickMapMarker={addPinClicked}
                            onIdleChanged={mapIdle}
                            onMapLoad={mapLoaded}
                            onMapMove={this.handleMapMove}
                            onMapMoveEnd={this.handleMapMoveEnd}
                            options={options}
                            setMapMoveState={setMapMoveState}
                            shouldFitbounds={
                                (fitBoundsOverride || !isBoundingBoxSearch) &&
                                !isRedirecting
                            }
                            showMapSearchButton={showMapSearchButton}
                            siteName={siteName}
                            zoom={zoom}
                            isTouchDevice={isTouchDevice}
                            page={page}
                            pageSize={pageSize}
                        />
                        <SearchAreaButton
                            isVisible={
                                isSearchButtonVisible && !shouldSearchOnMove
                            }
                            shouldDisableSearchButton={
                                shouldDisableSearchButton
                            }
                            onClick={this.handleSearch}
                            isLoading={loading && !isDesktop}
                        />
                    </Fragment>
                ) : (
                    <LoadingPlaceholder extraClasses="Map--loading" />
                )}
                {!isMobile && (
                    <div className="Map__autoSearch">
                        <Checkbox
                            className={classnames(
                                'Map__control',
                                onBackClick
                                    ? 'Map__control--auto-search-tablet'
                                    : 'Map__control--auto-search'
                            )}
                            data-wdio={'Map__autoSearch'}
                            key={'auto_search'}
                            name={'auto_search'}
                            checked={shouldSearchOnMove}
                            label={intl.formatMessage({
                                id: 'autoSearchLabel',
                                defaultMessage: 'Search when I move map'
                            })}
                            onChange={this.handleAutoSearchCheck}
                            size={'sm'}
                            inline
                        />
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = ({
    searchParams: {
        numFilters,
        isBoundingBoxSearch,
        params: {view}
    },
    map: {
        listingId,
        currentHitPrev,
        isSearchButtonVisible,
        shouldDisableSearchButton,
        bounds,
        mapMarkersClicked,
        mapCentralPin,
        shouldSearchOnMove,
        zoom,
        fitBoundsOverride
    },
    redirect: {isRedirecting},
    viewport: {breakpoint},
    system: {isMobile, isTablet, fullLocale}
}: ReduxState) => ({
    fullLocale,
    isTouchDevice: isMobile || isTablet,
    highlightedPin: listingId,
    currentHitPrev,
    numFilters,
    breakpoint,
    isRedirecting,
    isSearchButtonVisible,
    shouldDisableSearchButton,
    bounds,
    mapMarkersClicked,
    mapCentralPin,
    shouldSearchOnMove,
    fitBoundsOverride,
    zoom,
    isBoundingBoxSearch,
    isMapView: view === MAP_VIEW
});

const mapDispatchToProps = {
    changeHitPreview: mapAction.changeHitPreview,
    addPinClicked: mapAction.addPinClicked,
    mapLoaded: mapAction.mapLoaded,
    showMapSearchButton: mapAction.mapShowSearchButton,
    mapIdle: mapAction.mapIdle,
    resetSearchButton: mapAction.mapResetSearchButton,
    updateMap: mapAction.updateMap,
    mapSearch: routerActions.mapSearch,
    syncBounds: mapAction.syncBounds,
    enableSearchOnMove: mapAction.enableSearchOnMove,
    disableSearchOnMove: mapAction.disableSearchOnMove,
    setMapMoveState: mapAction.setMapMoveState,
    onClickFiltersButton: () => routerActions.pushModal(FILTERS_MODAL)
};

export default compose(
    injectIntl,
    connect(mapStateToProps, mapDispatchToProps)
)(Map);
